import { Stack, Divider } from 'braid-design-system';

import ReactionBar from 'src/components/atoms/ReactionBar/ReactionBar';
import ThreadContent from 'src/components/atoms/ThreadContent/ThreadContent';
import ThreadPersonalDetails from 'src/components/atoms/ThreadPersonalDetails/ThreadPersonalDetails';
import { handleAuthCheck } from 'src/core/utils/authUtil';
import type { ThreadCard } from 'src/mapper/threadMapper';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useLoginModal } from 'src/provider/loginModal';
import {
  updateThreadLikes,
  type updateThreadLikesParams,
} from 'src/services/threadInteractionService';

interface ThreadCardProps {
  thread: ThreadCard;
  isFeatured?: boolean;
  isCategoryThread?: boolean;
  onLikeSuccess: (id: string, countLabel: string, isLiked: boolean) => void;
}

export default ({ thread, onLikeSuccess }: ThreadCardProps) => {
  const {
    id,
    author,
    creationDate,
    description,
    category,
    exactSocialData,
    attachments,
    url,
    poll,
    liked,
  } = thread;

  const postingDate = creationDate;
  const images = attachments ?? [];

  const voted = poll?.options.some((option) => option?.voted || false) || false;

  const { trackAnalyticsInteraction } = useAnalytics();
  const { authStatus } = useAuthStatus();
  const { openLoginDialog } = useLoginModal();

  const handleLikeClick = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      const params: updateThreadLikesParams = {
        id: thread.id,
        liked: !thread.liked,
      };
      const { countLabel, isLiked } = await updateThreadLikes(params);
      onLikeSuccess(id, countLabel, isLiked);

      if (countLabel) {
        trackAnalyticsInteraction('LikeThreadClick', {
          threadId: thread.id,
          linkAction: !thread.liked ? 'like' : 'unlike',
        });
      }
    } catch {
      return;
    }
  };

  const handleCommentClick = () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    window.location.href = url;
    trackAnalyticsInteraction('CommentClick', {
      threadId: thread.id,
    });
  };

  return (
    <Stack space="xsmall" data={{ testid: 'thread-detail-card' }}>
      <ThreadPersonalDetails
        author={author}
        postingDate={postingDate}
        category={category}
        showJobTitle={false}
      />
      <Stack space="xsmall">
        <a key={id} href={url} style={{ textDecoration: 'none' }}>
          <ThreadContent
            text={description}
            images={images}
            poll={poll}
            maxLines={2}
            voted={voted}
          />
        </a>
      </Stack>
      <ReactionBar
        likeLabel={exactSocialData?.likeCountLabel}
        likeStatus={liked}
        onLikeClick={handleLikeClick}
        commentLabel={exactSocialData?.commentAndReplyCountLabel}
        onCommentClick={handleCommentClick}
      />
      <Divider weight="regular" />
    </Stack>
  );
};
